'use client';

import { logger } from '@adornis/base/logging.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { ArrowLeft, Bug, CheckCircle2, ImagePlus, Lightbulb, MessageSquare, Send, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { submitAppFeedbackMutation } from '../../../db/api/submit-app-feedback.js';
import { cn } from '../../lib/utils.js';
import { Button } from '../ui/button.js';

interface FeedbackPageProps {
  onBack: () => void;
}

type FeedbackType = 'bug' | 'feature' | 'general';

export function FeedbackPage({ onBack }: FeedbackPageProps) {
  const { t } = useTranslation();

  const [feedbackType, setFeedbackType] = useState<FeedbackType>('general');
  const [message, setMessage] = useState('');
  const [attachedImages, setAttachedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const validFiles: File[] = [];

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        alert(t('FeedbackPage.alerts.imageTooLarge', { name: file.name }));
        continue;
      }
      validFiles.push(file);
    }

    if (!validFiles.length) return;

    setAttachedImages(prev => [...prev, ...validFiles]);

    Promise.all(
      validFiles.map(
        file =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error(t('FeedbackPage.alerts.previewError', { name: file.name })));
            reader.readAsDataURL(file);
          }),
      ),
    )
      .then(newPreviews => {
        setImagePreviews(prev => [...prev, ...newPreviews]);
      })
      .catch(error => {
        logger.error(error, `Fehler beim Erstellen der Bildvorschau:`);
      });

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index: number) => {
    setAttachedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;

    setStatus('submitting');

    try {
      logger.info(
        `Sende Feedback: ${JSON.stringify({ feedback: message, type: feedbackType, imageCount: attachedImages.length })}`,
      );

      const imageFileIds = attachedImages.length
        ? await Promise.all(attachedImages.map(image => ImageFile.upload(image)))
        : [];

      await submitAppFeedbackMutation(feedbackType, message, imageFileIds)();

      setStatus('success');
    } catch (error) {
      logger.error(
        error,
        `Fehler beim Senden des Feedbacks: ${JSON.stringify({ feedback: message, type: feedbackType, imageCount: attachedImages.length })}`,
      );
      setStatus('idle');
      alert(t('FeedbackPage.alerts.submitError'));
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col h-full w-full bg-background overflow-y-auto">
        <div className="flex-1 px-4 sm:px-8 py-20 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mb-6">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">{t('FeedbackPage.success.title')}</h2>
          <p className="text-sm text-muted-foreground max-w-md mb-8">{t('FeedbackPage.success.description')}</p>
          <Button onClick={onBack} className="bg-brand-strong hover:bg-brand-700 text-white">
            {t('FeedbackPage.success.backButton')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-border bg-card px-4 sm:px-8 py-2 sm:py-4 shadow-sm">
        <div className="flex items-center justify-start gap-2 max-w-4xl mx-auto">
          <Button
            onClick={onBack}
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl sm:text-xl font-bold text-foreground tracking-tight">
            {t('FeedbackPage.header.title')}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 sm:px-8 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <p className="text-muted-foreground text-base">{t('FeedbackPage.content.description')}</p>

          {/* Kategorie-Auswahl */}
          <section className="space-y-3">
            <label className="text-sm font-semibold text-foreground">{t('FeedbackPage.categories.title')}</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                onClick={() => setFeedbackType('bug')}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left',
                  feedbackType === 'bug'
                    ? 'border-red-400 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-300'
                    : 'border-border bg-card hover:border-red-200 dark:hover:border-red-800 hover:bg-red-50/50 dark:hover:bg-red-900/10 text-muted-foreground',
                )}
              >
                <Bug className={cn('h-5 w-5', feedbackType === 'bug' ? 'text-red-500' : 'text-muted-foreground')} />
                <div>
                  <div className="text-sm font-semibold">{t('FeedbackPage.categories.bug.title')}</div>
                  <div className="text-xs opacity-80">{t('FeedbackPage.categories.bug.desc')}</div>
                </div>
              </button>

              <button
                onClick={() => setFeedbackType('feature')}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left',
                  feedbackType === 'feature'
                    ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-300'
                    : 'border-border bg-card hover:border-amber-200 dark:hover:border-amber-800 hover:bg-amber-50/50 dark:hover:bg-amber-900/10 text-muted-foreground',
                )}
              >
                <Lightbulb
                  className={cn('h-5 w-5', feedbackType === 'feature' ? 'text-amber-500' : 'text-muted-foreground')}
                />
                <div>
                  <div className="text-sm font-semibold">{t('FeedbackPage.categories.feature.title')}</div>
                  <div className="text-xs opacity-80">{t('FeedbackPage.categories.feature.desc')}</div>
                </div>
              </button>

              <button
                onClick={() => setFeedbackType('general')}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left',
                  feedbackType === 'general'
                    ? 'border-brand-strong bg-brand-surface text-brand-text'
                    : 'border-border bg-card hover:border-brand-border hover:bg-brand-surface/50 text-muted-foreground',
                )}
              >
                <MessageSquare
                  className={cn('h-5 w-5', feedbackType === 'general' ? 'text-brand-strong' : 'text-muted-foreground')}
                />
                <div>
                  <div className="text-sm font-semibold">{t('FeedbackPage.categories.general.title')}</div>
                  <div className="text-xs opacity-80">{t('FeedbackPage.categories.general.desc')}</div>
                </div>
              </button>
            </div>
          </section>

          {/* Text-Eingabe */}
          <section className="space-y-3">
            <label className="text-sm font-semibold text-foreground">{t('FeedbackPage.message.title')}</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={t('FeedbackPage.message.placeholder')}
              className="w-full min-h-[160px] p-4 text-sm rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:bg-background focus:outline-none focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-strong transition-colors resize-y"
            />
          </section>

          {/* Screenshot Upload */}
          <section className="space-y-3">
            <label className="text-sm font-semibold text-foreground">{t('FeedbackPage.upload.title')}</label>

            <div
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center justify-center text-muted-foreground hover:bg-accent hover:border-brand-strong hover:text-brand-strong transition-colors cursor-pointer"
            >
              <ImagePlus className="h-6 w-6 mb-2 opacity-80" />
              <span className="text-sm font-medium">{t('FeedbackPage.upload.clickToSelect')}</span>
              <span className="text-xs mt-1 opacity-70">{t('FeedbackPage.upload.formatConstraint')}</span>
            </div>

            {imagePreviews.length > 0 ? (
              <div className="space-y-3">
                <p className="text-sm font-semibold text-foreground">{t('FeedbackPage.upload.attachedImages')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {imagePreviews.map((preview, index) => (
                    <div
                      key={`${preview}-${index}`}
                      className="relative border border-border rounded-lg p-2 bg-muted"
                    >
                      <img src={preview} alt={`Preview ${index + 1}`} className="h-32 w-full rounded-md object-cover" />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute -top-3 -right-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-1.5 rounded-full hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors shadow-sm"
                        title={t('FeedbackPage.upload.removeImage')}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Verstecktes File-Input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageChange}
              multiple
              accept="image/png, image/jpeg, image/jpg"
              className="hidden"
            />
          </section>

          {/* Submit Button */}
          <div className="pt-6 border-t border-border flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={status === 'submitting' || !message.trim()}
              className="bg-brand-strong hover:bg-brand-700 text-white px-6 py-2 text-sm rounded-lg flex items-center gap-2"
            >
              {status === 'submitting' ? (
                t('FeedbackPage.submit.sending')
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  {t('FeedbackPage.submit.send')}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
